import {
  useRef,
  Fragment,
  useState,
  useEffect,
  type FC,
  type SyntheticEvent,
} from 'react';
import {
  useNavigate
} from 'react-router-dom';
import {
  useDispatch
} from 'react-redux';
import {
  Tab,
  Box,
  Tabs,
  Sheet,
  Stack,
  Table,
  TabList,
  Skeleton,
  styled,
  Typography,
  type StackProps,
} from '@mui/joy';
import {
  Swiper,
  SwiperSlide
} from 'swiper/react';
import SwiperCore from 'swiper';
import 'swiper/css';
import {
  setCurrentCategory,
  selectCurrentCategory,
  useTournamentsSelector,
  selectTournamentByCategory,
  selectTournamentsCategories,
  selectTournamentLeaderboardByCategory,
} from '@entities/tournaments';
import {
  leaderboardCupWithCloudsImg
} from '../assets';
import {
  TournamentThead
} from './tournament-thead.component';
import {
  TournamentTbodyRowItem
} from './tournament-tbody-row-item.component';

export type TournamentMiniLeaderboardProps = StackProps & {
  loading?: boolean
  withTabs?: boolean
};

const StyledSwiper = styled(Swiper)({
  width: '100%',
  paddingInline: 1
});

export const TournamentMiniLeaderboard: FC<TournamentMiniLeaderboardProps> = ({
  sx,
  loading,
  ...props
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const swiperRef = useRef<SwiperCore | null>(null);

  const categories = useTournamentsSelector(selectTournamentsCategories);
  const currentCategory = useTournamentsSelector(selectCurrentCategory);
  const leaderboardItems = useTournamentsSelector(selectTournamentLeaderboardByCategory(currentCategory ?? ''));
  const tournamentByCategory = useTournamentsSelector(selectTournamentByCategory(currentCategory ?? ''));

  const [activeIndex, setActiveIndex] = useState<number>(() => {
    const initialIndex = categories.indexOf(currentCategory ?? '');
    return initialIndex >= 0 ? initialIndex : 0;
  });

  useEffect(() => {
    const index = categories.indexOf(currentCategory ?? '');
    if (index >= 0 && index !== activeIndex) {
      setActiveIndex(index);
      swiperRef.current?.slideTo(index);
    }
  }, [currentCategory, categories, activeIndex]);

  const handleTabChange = (_event: SyntheticEvent | null, newValue: string | number | null) => {
    if (typeof newValue === 'string') {
      const index = categories.indexOf(newValue);
      if (index >= 0) {
        setActiveIndex(index);
        dispatch(setCurrentCategory(newValue));
      }
    }
  };

  const handleSwiperSlideChange = (swiper: SwiperCore) => {
    const newIndex = swiper.activeIndex;
    if (newIndex !== activeIndex) {
      setActiveIndex(newIndex);
      dispatch(setCurrentCategory(categories[newIndex]));
    }
  };

  const handleNavigateToTournamentDetails = () => {
    navigate(tournamentByCategory?.buttonUrl || tournamentByCategory?.imageUrl || '/lobby');
  };

  return !!categories.length && (
    <Stack
      sx={[
        ({ breakpoints }) => ({
          width: '100%',
          [breakpoints.up(1024)]: {
            display: 'none',
          }
        }),
        ...(sx ? Array.isArray(sx) ? sx : [sx] : [])
      ]}
      {...props}>
      <Tabs
        variant='soft'
        onChange={handleTabChange}
        value={categories[activeIndex]}
        sx={{ marginBottom: 1 }}>
        <TabList
          variant='soft'
          disableUnderline>
          {categories.map((category) => (
            <Tab
              variant='soft'
              key={category}
              value={category}
              sx={{ width: '100%' }}>
              {category}
            </Tab>
          ))}
        </TabList>
      </Tabs>
      <StyledSwiper
        spaceBetween={5}
        onSwiper={(swiper) => { swiperRef.current = swiper }}
        initialSlide={activeIndex}
        onSlideChange={handleSwiperSlideChange}>
        {categories.map((category) => (
          <SwiperSlide key={category}>
            <Sheet
              sx={({ palette }) => ({
                width: '100%',
                height: '100%',
                borderWidth: 1,
                borderRadius: 8,
                overflow: 'hidden',
                borderStyle: 'solid',
                position: 'relative',
                borderColor: 'transparent',
                backgroundColor: palette.common[900],
              })}>
              {(leaderboardItems?.length && !loading) ? (
                <Table
                  size='md'
                  variant='plain'
                  stickyHeader
                  sx={({ palette, breakpoints }) => ({
                    gap: 1.25,
                    width: '100%',
                    '& thead': {
                      height: 28,
                    },
                    '& thead th': {
                      zIndex: 1,
                      fontSize: 14,
                      height: 28,
                      fontWeight: 400,
                      lineHeight: '20px',
                      fontStyle: 'normal',
                      paddingInlineEnd: 2,
                      verticalAlign: 'middle',
                      color: palette.common[150],
                      [breakpoints.down(491)]: {
                        paddingInline: 1,
                      }
                    },
                    '& tbody td, & tfoot th': {
                      fontSize: 12,
                      fontWeight: 500,
                      lineHeight: '20px',
                      fontStyle: 'normal',
                      paddingInlineEnd: 2,
                      color: palette.common.white,
                      [breakpoints.down(491)]: {
                        paddingInline: 1,
                      }
                    },
                  })}>
                  <TournamentThead />
                  <tbody>
                    {leaderboardItems.map((leaderboardItem, index) => (
                      <TournamentTbodyRowItem
                        leaderboardItem={leaderboardItem}
                        key={`${leaderboardItem.rank}-${leaderboardItem.player}-${index}`}
                      />
                    ))}
                  </tbody>
                  <tfoot onClick={handleNavigateToTournamentDetails}>
                    <tr className='call-to-action'>
                      <th colSpan={4}>
                        View All
                      </th>
                    </tr>
                  </tfoot>
                </Table>
              ) : (
                <Fragment>
                  {loading ? (
                    <Skeleton
                      variant='rectangular'
                      sx={{
                        height: 112,
                        width: '100%',
                        borderRadius: 8
                      }}
                    />
                  ) : (
                    <Stack
                      alignItems='center'
                      sx={({ palette }) => ({
                        padding: 2,
                        minHeight: 112,
                        backgroundColor: palette.common[565],
                      })}>
                      <Box
                        width={100}
                        height={45}
                        component='img'
                        src={leaderboardCupWithCloudsImg.default}
                      />
                      <Typography
                        sx={({ palette }) => ({
                          width: '100%',
                          maxWidth: 270,
                          fontWeight: 500,
                          textAlign: 'center',
                          fontSize: 12,
                          color: palette.common.white,
                        })}>
                        The {category} Race hasn't started yet. Stay tuned and be the first to take the lead!
                      </Typography>
                    </Stack>
                  )}
                </Fragment>
              )}
            </Sheet>
          </SwiperSlide>
        ))}
      </StyledSwiper>
    </Stack>
  );
};
